<script setup>
import {computed, inject, onMounted, ref, watch} from 'vue'
import { useFormFieldPrefill } from '@/composables/useFormFieldPrefill'

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        require: true
    },
    content: {
        type: Object,
        required: true
    },
});

const fieldUuid = computed(() => props.content.uuid);
const title = computed(() => props.content.title);
const required = computed(() => props.content.required);
const selectedValue = ref(1);
const minValue = computed(() => props.content.minValue || 1);
const maxValue = computed(() => props.content.maxValue || 5);

const valueStops = computed(() => {
    const stops = [];
    const min = parseInt(minValue.value);
    const max = parseInt(maxValue.value);
    if (max > 16) {
        const step = Math.max(1, Math.floor((max - min)));
        for (let i = min; i <= max; i += step) {
            stops.push(i);
        }
        if (stops[stops.length - 1] !== max) {
            stops.push(max);
        }
    } else {
        for (let i = min; i <= max; i++) {
            stops.push(i);
        }
    }
    return stops;
})

const formDatastore = inject('formDatastore');
watch(() => selectedValue.value, (value) => {
    const normalizedValue = value === null || value === undefined ? null : Number(value)

    if (formDatastore) {
        formDatastore.fields[props.uuid] = Number.isNaN(normalizedValue) ? null : normalizedValue
    }
}, {immediate: true});

const { fieldValue } = useFormFieldPrefill(props.uuid, props.content)

watch(() => fieldValue, () => {
    selectedValue.value = fieldValue.value
}, {deep: true})

</script>

<template>
    <div class="relative mt-12">
        <input
            type="hidden"
            :name="`data[${uuid}][block_uuid]`"
            :value="fieldUuid"
        />

        <input
            type="hidden"
            :name="`data[${uuid}][value]`"
            :required="required"
            :value="selectedValue"
            :content="fieldValue"
        />

        <input-label
            :label="title"
            :required="required"
        />
        <div class="px-4">
            <input-range
                v-model:content="selectedValue"
                :label="title"
                :required="required"
                :min="minValue"
                :max="maxValue"
                :step="1"
                :show-selected-value="true"
            />

            <div class="flex justify-between">
                <div
                    v-for="n in valueStops"
                    :key="n"
                    class="h-4 relative"
                >
                        <span
                            class="transform -translate-x-1/2 text-xs text-gray-400"
                        >{{ n }}</span>
                </div>
            </div>

            <div class="flex justify-between mb-4 text-3xl mt-2">
                <span class="transform -translate-x-1">{{ content.lowSymbol }}</span>
                <span>{{ content.midSymbol }}</span>
                <span class="transform translate-x-1">{{ content.highSymbol }}</span>
            </div>
        </div>

        <page-builder-block-view-forms-sidebar
            v-if="content.showInstructions"
            :container-ref="containerRef"
            :uuid="uuid"
            :content="content"
        />
    </div>
</template>
